import { doc, getDoc } from '../services/firestoreCompat';
import { db } from './firebaseConfig';

export interface DeliveryZone {
  postal_code: string;
  min_order: number;
  delivery_fee: number;
  free_threshold: number;
  available: boolean;
  city?: string;
}

export interface DeliveryQuote {
  allowed: boolean;
  reason?: 'no_zone' | 'disabled' | 'below_min';
  zone: DeliveryZone | null;
  fee: number;
  isFree: boolean;
  amountBelowMin: number;
  amountToFreeDelivery: number;
}

const zoneCache = new Map<string, { zone: DeliveryZone | null; fetchedAt: number }>();
const CACHE_TTL = 60 * 1000;

function normalizeZip(raw: string): string {
  return (raw || '').toString().trim().replace(/\s+/g, '');
}

export async function loadDeliveryZone(postalCode: string): Promise<DeliveryZone | null> {
  const zip = normalizeZip(postalCode);
  if (!zip) return null;

  const cached = zoneCache.get(zip);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) return cached.zone;

  try {
    const snap = await getDoc(doc(db, 'delivery_zones', zip));
    if (!snap.exists()) {
      zoneCache.set(zip, { zone: null, fetchedAt: Date.now() });
      return null;
    }
    const data = snap.data();
    const zone: DeliveryZone = {
      postal_code: data.postal_code || zip,
      min_order: Number(data.min_order) || 0,
      delivery_fee: Number(data.delivery_fee) || 0,
      free_threshold: Number(data.free_threshold) || 0,
      available: data.available !== false,
      city: data.city || '',
    };
    zoneCache.set(zip, { zone, fetchedAt: Date.now() });
    return zone;
  } catch (e) {
    console.warn('[DELIVERY ZONES] load error:', e);
    return null;
  }
}

export function quoteDelivery(zone: DeliveryZone | null, subtotal: number): DeliveryQuote {
  if (!zone) {
    return {
      allowed: false, reason: 'no_zone', zone: null,
      fee: 0, isFree: false, amountBelowMin: 0, amountToFreeDelivery: 0,
    };
  }
  if (!zone.available) {
    return {
      allowed: false, reason: 'disabled', zone,
      fee: 0, isFree: false, amountBelowMin: 0, amountToFreeDelivery: 0,
    };
  }
  const below = Math.max(0, zone.min_order - subtotal);
  if (below > 0) {
    return {
      allowed: false, reason: 'below_min', zone,
      fee: zone.delivery_fee, isFree: false,
      amountBelowMin: below,
      amountToFreeDelivery: Math.max(0, zone.free_threshold - subtotal),
    };
  }
  const isFree = zone.free_threshold > 0 && subtotal >= zone.free_threshold;
  return {
    allowed: true, zone,
    fee: isFree ? 0 : zone.delivery_fee,
    isFree,
    amountBelowMin: 0,
    amountToFreeDelivery: isFree ? 0 : Math.max(0, zone.free_threshold - subtotal),
  };
}
